import { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { EmptyState, Loading } from "../../components/shared";
import { toast } from "sonner";
import { Download, FileText, UploadCloud } from "lucide-react";

const DOCUMENT_TYPES = [
  "Business Registration",
  "GST Certificate",
  "PAN",
  "Address Proof",
  "Bank Proof / Cancelled Cheque",
  "Licence / Certification",
  "Insurance",
  "Experience Certificate",
  "Technician Photo ID",
  "Udyam / MSME Certificate",
  "Other",
];

export default function VendorDocuments() {
  const [documents, setDocuments] = useState(null);
  const [docType, setDocType] = useState(DOCUMENT_TYPES[0]);
  const [replacement, setReplacement] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/vendor/documents").then((response) => setDocuments(response.data));
  };
  useEffect(() => {
    load();
  }, []);
  if (!documents) return <Loading />;

  const upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const data = new FormData();
    data.append("file", file);
    data.append("doc_type", docType);
    if (replacement) data.append("replaces_document_id", replacement);
    try {
      await api.post("/vendor/documents", data);
      setReplacement("");
      load();
      toast.success("Document uploaded for review");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };

  return (
    <section data-testid="vendor-documents-page">
      <div className="border-b border-slate-200 pb-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Private verification records</p>
        <h1 className="mt-2 font-display text-3xl font-bold">Documents</h1>
        <p className="mt-2 text-sm text-slate-600">Documents are available only to your account and authorized MySocietyMitra reviewers.</p>
      </div>
      <div className="mt-6 border border-slate-200 bg-white p-5 sm:p-7">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="mb-1.5 block" htmlFor="vendor-document-type">Document type</Label>
            <select
              className="h-10 w-full border border-slate-300 bg-white px-3 text-sm"
              data-testid="vendor-document-type"
              id="vendor-document-type"
              onChange={(event) => setDocType(event.target.value)}
              value={docType}
            >
              {DOCUMENT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
          </div>
          <div>
            <Label className="mb-1.5 block" htmlFor="vendor-document-replacement">Replace rejected document</Label>
            <select
              className="h-10 w-full border border-slate-300 bg-white px-3 text-sm"
              data-testid="vendor-document-replacement"
              id="vendor-document-replacement"
              onChange={(event) => setReplacement(event.target.value)}
              value={replacement}
            >
              <option value="">Not a replacement</option>
              {documents.filter((item) => item.review_status === "REJECTED").map((item) => (
                <option key={item.vdoc_id} value={item.vdoc_id}>{item.doc_type} · {item.filename}</option>
              ))}
            </select>
          </div>
        </div>
        <label className="mt-5 inline-flex cursor-pointer items-center gap-2 bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800" data-testid="vendor-document-upload-label">
          <input accept=".pdf,image/*" className="sr-only" data-testid="vendor-document-upload-input" onChange={upload} type="file" />
          <UploadCloud className="h-4 w-4" />
          {busy ? "Uploading…" : "Upload document"}
        </label>
      </div>
      <div className="mt-6 space-y-3">
        {documents.length === 0 ? <EmptyState icon={FileText} subtitle="Upload verification documents." title="No documents" /> : documents.map((document) => (
          <article className="flex flex-wrap items-center justify-between gap-4 border border-slate-200 bg-white p-5" data-testid={`vendor-document-${document.vdoc_id}`} key={document.vdoc_id}>
            <div className="min-w-0">
              <p className="font-semibold">{document.doc_type}</p>
              <p className="mt-1 truncate text-sm text-slate-500">{document.filename}</p>
              {document.review_status === "REJECTED" && document.review_reason && (
                <p className="mt-2 text-sm text-rose-700" data-testid={`vendor-document-reason-${document.vdoc_id}`}>
                  Correction needed: {document.review_reason}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-slate-600" data-testid={`vendor-document-status-${document.vdoc_id}`}>
                {(document.review_status || "PENDING").replaceAll("_", " ")}
              </span>
              <a
                className="inline-flex h-9 w-9 items-center justify-center border border-slate-200 text-slate-700 hover:bg-slate-50"
                data-testid={`vendor-document-download-${document.vdoc_id}`}
                href={`${API}/vendor/documents/${document.vdoc_id}/download`}
                title="Download document"
              >
                <Download className="h-4 w-4" />
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}